import { type FC, type ReactNode, useState, useReducer, useRef, useMemo, createContext, useContext } from "react";
import { useZxing } from "react-zxing";
import { prepareZXingModule, type BarcodeFormat } from "barcode-detector/ponyfill";

import { annotate, If } from "./utils";

// zxing-wasm defaults to fetching its wasm from the jsdelivr CDN; point it at
// our own copy (put in static/ by the build scripts) so the scanner works
// offline. This must run at module scope: the BarcodeDetector constructor
// starts the wasm fetch as soon as react-zxing's useBarcodeDetector runs,
// before any hook effect could apply an override.
prepareZXingModule({
  overrides: {
    locateFile: (path: string, prefix: string) =>
      path.endsWith(".wasm") ? "/static/zxing_reader.wasm" : prefix + path,
  },
});

export interface ScanResult {
  format: string,
  text: string,
}

export type OnScanResultCallback = (a: ScanResult | null) => void;

export interface ScannerDefaults {
  allFormats?: boolean;
}

export type StartScanner = (a: OnScanResultCallback, defaults?: ScannerDefaults) => void;

export const StartScannerContext = createContext(
  annotate<StartScanner>(
    _ => {
      throw "StartScannerContext grabbed when none available"
    }
  )
);

export interface useScannerReturn {
  startScanner: () => void;
}

export function useScanner({ onScanResult, defaults }: { onScanResult: OnScanResultCallback; defaults?: ScannerDefaults }): useScannerReturn {
  const ctx = useContext(StartScannerContext);
  return {
    startScanner() {
      ctx(onScanResult, defaults);
    },
  };
}

const QR_FORMATS: BarcodeFormat[] = ["qr_code", "micro_qr_code", "rm_qr_code"];
const DM_FORMATS: BarcodeFormat[] = ["data_matrix"];
const CODE128_FORMATS: BarcodeFormat[] = ["code_128"];
const OTHER_FORMATS: BarcodeFormat[] = [
  "aztec", "codabar", "code_32", "code_39", "code_93",
  "databar", "databar_expanded", "databar_limited", "databar_stacked",
  "dx_film_edge", "ean_13", "ean_8", "itf",
  "maxi_code", "pdf417", "upc_a", "upc_e",
  "telepen", "other_barcode",
];

const DEFAULT_TOGGLES = { qr: true, dm: true, code128: true, other: false };

function reducer(state: Array<[string, string]>, action: [string, string] | "reset"): Array<[string, string]> {
  if (action === "reset") {
    return [];
  }
  for (const existing of state) {
    if (existing[0] === action[0] && existing[1] === action[1]) {
      return state;
    }
  }
  if (state.length == 20) {
    return [action];
  } else {
    return [...state, action];
  }
}

export const Scanner: FC<{children: ReactNode}> = ({children}) => {
  const onScanResultRef = useRef(annotate<OnScanResultCallback>(
    _ => {
      throw "onScanResultRef called before it was set"
    }
  ));
  const [enabled, setEnabled] = useState(false);
  const [scans, addScan] = useReducer(reducer, annotate<[string, string][]>([]));
  const [toggles, setToggles] = useState(DEFAULT_TOGGLES);

  const formats = useMemo<BarcodeFormat[] | undefined>(() => {
    if (toggles.qr && toggles.dm && toggles.code128 && toggles.other) return undefined;
    return [
      ...(toggles.qr ? QR_FORMATS : []),
      ...(toggles.dm ? DM_FORMATS : []),
      ...(toggles.code128 ? CODE128_FORMATS : []),
      ...(toggles.other ? OTHER_FORMATS : []),
    ];
  }, [toggles]);

  const { ref } = useZxing({
    paused: !enabled,
    timeBetweenDecodingAttempts: 50,
    ...(formats !== undefined ? { formats } : {}),
    onDecodeResult(result) {
      console.log("onDecodeResult", result);
      addScan([result.format, result.rawValue]);
    },
    onDecodeError(e) {
      console.log("onDecodeError", e);
    },
    onError(e) {
      console.log("onError", e);
    },
  });

  const startScanner = (cb: OnScanResultCallback, defs?: ScannerDefaults) => {
    onScanResultRef.current = cb;
    setToggles(defs?.allFormats ? { qr: true, dm: true, code128: true, other: true } : DEFAULT_TOGGLES);
    setEnabled(true);
  };

  const makeStopCallback = (result: ScanResult | null) => (
    () => {
      setEnabled(false);
      addScan("reset");
      onScanResultRef.current(result);
    }
  );

  const toggle = (key: keyof typeof DEFAULT_TOGGLES) => {
    setToggles(t => ({ ...t, [key]: !t[key] }));
  };

  return <div className="stack-container" style={{width: "100%", height: "100%"}}>
    <div className="stack-item">
      <StartScannerContext value={startScanner}>
        {children}
      </StartScannerContext>
    </div>
    <If condition={enabled}>
      <div className="stack-item" style={{display: "flex", flexDirection: "column", position: "fixed"}}>
        <video ref={ref} style={{maxHeight: "100%"}} />
      </div>
      <div className="stack-item" style={{display: "flex", flexDirection: "column-reverse", position: "fixed"}}>
        <div style={{display: "flex", flexDirection: "row", backgroundColor: "white", padding: "2px", borderTop: "1px solid black", justifyContent: "space-between"}}>
          <div style={{display: "flex", gap: "6px", alignItems: "center", padding: "0 4px"}}>
            {(["qr", "dm", "code128", "other"] as const).map(key => (
              <label key={key} style={{display: "flex", alignItems: "center", gap: "3px", fontSize: "small", cursor: "pointer"}}>
                <input type="checkbox" checked={toggles[key]} onChange={() => toggle(key)} />
                {key === "qr" ? "QR" : key === "dm" ? "DM" : key === "code128" ? "Code128" : "Other"}
              </label>
            ))}
          </div>
          <button onClick={makeStopCallback(null)} type="button">Cancel</button>
        </div>
        {scans.map((scan) => (
          <div key={`${scan[0]}-${scan[1]}`} className="scanner-result-item" style={{display: "flex", flexDirection: "row", justifyContent: "end", backgroundColor: "white", padding: "2px", borderTop: "1px solid black", alignItems: "center", textAlign: "center"}}>
            <div style={{flexGrow: "0", flexShrink: "1"}}>{scan[0]}</div>
            <div style={{fontFamily: "monospace", whiteSpace: "pre-wrap", flexGrow: "1", flexShrink: "2"}}>{scan[1]}</div>
            <button type="button" style={{flexShrink: "0", flexGrow: "0"}} onClick={makeStopCallback({format: scan[0], text: scan[1]})}>GO</button>
          </div>
        ))}
      </div>
    </If>
  </div>
};

export default Scanner;
